import * as React from 'react';
import { Modal, Form as SemanticForm, Message, Button } from 'semantic-ui-react';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { buildParametersValidationSchema } from './ParameterField';
import { Parameters } from './Parameters';
import { HandlerRegistry } from './RunTypes/HandlerRegistry';
import SimpleParameters from './SimpleParameters';
import { CaseClient, CaseRunCreateModelBase, RunParameterModel } from '@app/api2/api';
import { param } from 'jquery';
import { BPButton } from '@/components/bp/bpbutton';

export interface ICreateCaseRunCompactProps {
    labId: number;
    caseId: number;
    evidenceId?: number;
    referenceId?: number;
    runType?: string; // default 'dnamatch'
    onSuccess?: (res: any) => void;
    onCancel?: () => void;
}

export interface ICreateCaseRunCompactState {
    show: boolean;
    createModel: CaseRunCreateModelBase | null;
    populateId?: number;
    // parameterValues?: { [key: string]: any };
    creatingError?: string;
    isParametersValid: boolean;
}

const caseClient = new CaseClient();

export class CreateCaseRunCompact extends React.Component<ICreateCaseRunCompactProps, ICreateCaseRunCompactState> {
    runType = this.props.runType ?? 'dnamatch';
    handler = HandlerRegistry.getRunTypeHandler(this.runType);

    constructor(props: ICreateCaseRunCompactProps) {
        super(props);
        this.state = { show: false, createModel: null, isParametersValid: true };
    }

    open = async () => {
        try {
            const daily = !this.props.caseId ? await caseClient.createDaily(this.props.labId) : { caseId: this.props.caseId };
            const model = await this.handler.createBlankModel({ labId: this.props.labId, caseId: daily.caseId, runType: this.runType, evidenceId: this.props.evidenceId, ednaTestId: null });

            if (this.props.referenceId && 'references' in model && Array.isArray(model.references)) {
                const ref = model.references.find(r => r.id == this.props.referenceId);
                if (ref)
                    ref.selected = true;
            }

            if (this.handler) {
                let model2 = this.handler.applyInformationValuesToModel(model as any, model);
            }

            const extSearch = model.parameters.find(p => p.parameter.toLowerCase().endsWith('useextendedsearch'));
            if (extSearch) {
                extSearch.userEditable = false;
                extSearch.value = '1';
            }

            this.setState({ createModel: model, show: true, creatingError: undefined });
        } catch (e) {
            this.setState({ creatingError: e && e.message ? e.message : String(e) });
        }
    }

    close = () => {
        this.setState({ show: false, createModel: null, creatingError: undefined });
        if (this.props.onCancel) this.props.onCancel();
    }

    render() {
        if (!this.state.show || !this.state.createModel) return null;

        const initialPopulation = this.state.createModel.populationId ? String(this.state.createModel.populationId) : '';

        const initialValues = {
            populationId: initialPopulation,
            parameters: this.state.createModel.parameters
        };

        const validationSchema: Yup.SchemaOf<typeof initialValues> = Yup.object().shape({
            populationId: Yup.string().required('Population is required'),
            parameters: Yup.array().required() as any,
        });

        return (
            <Formik
                initialValues={initialValues}
                enableReinitialize={false}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting, setFieldError }) => {
                    // console.log('Submitting create case run with values:', this.state.createModel, values);
                    // return;
                    if (!this.state.createModel) return;
                    this.setState({ creatingError: undefined });
                    try {
                        const daily = !this.props.caseId ? await caseClient.createDaily(this.props.labId) : { caseId: this.props.caseId };

                        const res = await this.handler.save({ labId: this.props.labId, caseId: daily.caseId, model: this.state.createModel as any });
                        setSubmitting(false);
                        this.setState({ show: false, createModel: null });
                        if (this.props.onSuccess) this.props.onSuccess(res);
                    } catch (e) {
                        setSubmitting(false);
                        const err = e && e.message ? e.message : String(e);
                        this.setState({ creatingError: err });
                    }
                }}
            >
                {({ values, setFieldValue, isSubmitting, errors, isValid, setFieldError, setFieldTouched }) => (
                    <Modal as={FormikForm} open={true} size="small" onClose={this.close}>
                        <Modal.Header>Create {this.state.createModel.runType} case run</Modal.Header>
                        <Modal.Content>

                            <SemanticForm>
                                <SemanticForm.Field>
                                    <label>Population</label>
                                    <select value={values.populationId ?? ''} onChange={e => {
                                        setFieldValue('populationId', e.target.value);
                                        this.setState(prevState => ({
                                            createModel: {
                                                ...prevState.createModel,
                                                populationId: e.target.value ? parseInt(e.target.value) : undefined
                                            }
                                        }));
                                    }}>
                                        <option value="">(none)</option>
                                        {(this.state.createModel.populations || []).map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                                    </select>
                                </SemanticForm.Field>

                                <SemanticForm.Field>
                                    <label>Parameters</label>
                                    <SimpleParameters
                                        caseRunCreateModel={this.state.createModel}
                                        labId={this.props.labId}
                                        parameters={this.state.createModel.parameters || []}
                                        onValidationChanged={isValid => {
                                            this.setState({ ...this.state, isParametersValid: isValid });
                                        }}
                                        onParametersChanged={async vals => {
                                            await setFieldValue('parameters', vals);

                                            this.setState(prevState => ({
                                                createModel: {
                                                    ...prevState.createModel,
                                                    parameters: vals
                                                }
                                            }));
                                        }}
                                        disabled={isSubmitting}
                                        secondaryParametersCollapsed={true}
                                        parameterRenderer={this.handler}
                                    />
                                </SemanticForm.Field>
                                {/* <pre>{JSON.stringify(errors, null, 2)}</pre> */}

                                {this.state.creatingError && <Message negative>{this.state.creatingError}</Message>}
                            </SemanticForm>


                        </Modal.Content>

                        <Modal.Actions>
                            <BPButton onClick={this.close} disabled={isSubmitting}>{window.translation.common.cancel}</BPButton>
                            <BPButton primary type="submit" disabled={isSubmitting || !isValid || !this.state.isParametersValid} loading={isSubmitting}>{window.translation.common.search}</BPButton>
                        </Modal.Actions>
                    </Modal>
                )}
            </Formik>
        );
    }
}

export default CreateCaseRunCompact;
